import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShareIcon from "@mui/icons-material/Share";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    Popover,
    Stack,
    TextField,
    Typography,
    Tabs,
    Tab,
    Chip,
    Card,
    CardContent,
    CardActions,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useAppStateContext } from "../../state/AppContextProvider";
import { HttpServiceContext } from "../../state/HttpProvider";
import { ChatHistory, getCurrentOrchestration, OrchestrationState, ConversationListItem } from "../../common/models";
import { ChatBubbleOutlineRounded } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faPencil, faTrashCan, faShare } from "@fortawesome/free-solid-svg-icons";
import { times } from "lodash";
import { faComment } from "@fortawesome/free-regular-svg-icons";

// Map agent names to display labels
const getAgentDisplayName = (agentName: string | null | undefined): string | null => {
    if (!agentName) return null;
    
    const agentMap: Record<string, string> = {
        'NegotiatorAgent': 'Gather Intel',
        'Negotiator Agent': 'Gather Intel',
        'POVAgent': 'POV',
        'POV Agent': 'POV',
        'RiskAssessmentAgent': 'Risk Assessment',
        'Risk Assessment Agent': 'Risk Assessment',
        'OneSSheetAgent': 'One Sheet',
        'OneSheet Agent': 'One Sheet',
        'SimulationAgent': 'Simulation',
        'Simulation Agent': 'Simulation',
        'CaviaarAgent': 'Caviaar',
        'Caviaar Agent': 'Caviaar',
        'SituationAssessmentAgent': 'Situation Assessment',
        'Situation Assessment Agent': 'Situation Assessment',
        'GradingAgent': 'Grading',
        'Grading Agent': 'Grading',
    };
    
    return agentMap[agentName] || agentName;
};

type ChatType = {
    title: string;
    time: string;
    meta?: any;
    timestamp: string;
    conversationId: string;
    createdAt?: string;
    lastModifiedAt?: string;
    isOwner?: boolean;
    isShared?: boolean;
    sharedWith?: string[];
    currentAgent?: string | null;
    orchestrationCount?: number;
};

interface HistorySection {
    title: string;
    chats: ChatType[];
}

const MuiChatHistoryBar = () => {
    const appStateContext = useAppStateContext();
    const { httpService } = useContext(HttpServiceContext);
    const history: ChatHistory | null = appStateContext.state.chatHistory;
    const [formattedHistoryData, setFormattedHistoryData] = useState<HistorySection[]>([]);
    
    // Enhanced state for new features
    const [currentTab, setCurrentTab] = useState<'mine' | 'shared'>('mine');
    const [ownedConversations, setOwnedConversations] = useState<ConversationListItem[]>([]);
    const [sharedConversations, setSharedConversations] = useState<ConversationListItem[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [itemToBeDeleted, setItemToBeDeleted] = useState<string | null>(null);
    const [itemToBeShared, setItemToBeShared] = useState<ChatType | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareEmails, setShareEmails] = useState<string>('');
    const [shareError, setShareError] = useState<string>('');

    const [searchValue, setSearchValue] = useState<string>("");

    const [mount, setMount] = useState(false);
    useEffect(() => {
        setTimeout(() => {
            setMount(true);
        }, 1000)
    }, [])

    // Fetch conversation list with metadata
    const fetchConversationList = async () => {
        try {
            setIsRefreshing(true);
            const response = await httpService.getConversationList(new AbortController().signal);
            console.log('Conversation list response:', response);
            console.log('Owned conversations with agents:', response.ownedConversations.map(c => ({ 
                sessionId: c.sessionId, 
                title: c.title, 
                currentAgent: c.currentAgent 
            })));
            setOwnedConversations(response.ownedConversations);
            setSharedConversations(response.sharedConversations);
        } catch (error) {
            console.error('Failed to fetch conversation list:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    // Load conversation list on mount and when history opens
    useEffect(() => {
        if (mount && appStateContext.state.isChatHistoryOpen) {
            fetchConversationList();
            
            // Set up polling to refresh titles every 2 minutes when history is open
            const pollInterval = setInterval(() => {
                fetchConversationList();
            }, 120000); // 2 minutes
            
            return () => clearInterval(pollInterval);
        }
        return undefined;
    }, [mount, appStateContext.state.isChatHistoryOpen]);

    // Handle share conversation
    const handleShareConversation = async () => {
        if (!itemToBeShared) return;

        const emails = shareEmails.split(',').map(e => e.trim()).filter(e => e);
        if (emails.length === 0) {
            setShareError('Please enter at least one email address');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidEmails = emails.filter(e => !emailRegex.test(e));
        if (invalidEmails.length > 0) {
            setShareError(`Invalid email(s): ${invalidEmails.join(', ')}`);
            return;
        }

        try {
            console.log('🔄 Sharing conversation:', {
                sessionId: itemToBeShared.conversationId,
                emailAddresses: emails
            });
            
            const response = await httpService.shareConversation({
                sessionId: itemToBeShared.conversationId,
                emailAddresses: emails,
            }, new AbortController().signal);
            
            console.log('✅ Share response:', response);
            
            // Check if the response indicates success
            if (response && !response.success) {
                setShareError(response.message || 'Failed to share conversation');
                return;
            }
            
            // Refresh the list
            await fetchConversationList();
            
            // Close dialog and reset
            setShareDialogOpen(false);
            setShareEmails('');
            setShareError('');
            setItemToBeShared(null);
        } catch (error) {
            console.error('❌ Failed to share conversation:', error);
            setShareError(`Failed to share conversation: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    const handleRefresh = async () => {
        await fetchConversationList();
        await appStateContext.getChatHistory();
    };

    useEffect(() => {
        const sectionCache: { [key: string]: HistorySection } = {};
        
        // Determine which conversations to show based on current tab
        const conversationsToShow = currentTab === 'mine' ? ownedConversations : sharedConversations;
        
        // Merge history with metadata
        const conversationMap = new Map(conversationsToShow.map(c => [c.sessionId, c]));

        // For shared tab, we need to show conversations even if they're not in local history yet
        if (currentTab === 'shared') {
            // Show all shared conversations from metadata
            conversationsToShow.forEach((metadata) => {
                const historyEntry = history?.chatHistory?.[metadata.sessionId];
                
                // Use lastModifiedAt from metadata for timestamp
                const date = metadata.lastModifiedAt ? new Date(metadata.lastModifiedAt) : new Date();
                
                // Check if date is today, yesterday, within the last 7 days, or older
                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                const last7Days = new Date(today);
                last7Days.setDate(last7Days.getDate() - 7);

                let section = 'Older';
                if (date.toDateString() === today.toDateString()) {
                    section = 'Today';
                } else if (date.toDateString() === yesterday.toDateString()) {
                    section = 'Yesterday';
                }
                else if (date > last7Days) {
                    section = 'Last 7 Days';
                }

                // Format timestamp to be H:MM AM/PM
                const hours = date.getHours();
                const minutes = date.getMinutes();
                const ampm = hours >= 12 ? 'PM' : 'AM';
                let chatDateTimeFormatted = `${hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours)}:${minutes < 10 ? `0${minutes}` : minutes} ${ampm}`;

                if (['Older', 'Last 7 Days'].includes(section)) {
                    const formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
                    chatDateTimeFormatted = `${formattedDate} ${chatDateTimeFormatted}`;
                }

                const contentTitle = metadata.title || 'Shared Conversation';
                
                if (sectionCache.hasOwnProperty(section)) {
                    sectionCache[section].chats.push({
                        title: contentTitle,
                        time: chatDateTimeFormatted,
                        timestamp: metadata.lastModifiedAt || date.toISOString(),
                        conversationId: metadata.sessionId,
                        createdAt: metadata.createdAt,
                        lastModifiedAt: metadata.lastModifiedAt,
                        isOwner: metadata.isOwner,
                        isShared: metadata.isShared,
                        currentAgent: metadata.currentAgent,
                        sharedWith: metadata.sharedWith,
                        orchestrationCount: metadata.orchestrationCount,
                    });
                } else {
                    sectionCache[section] = {
                        title: section,
                        chats: [{
                            title: contentTitle,
                            time: chatDateTimeFormatted,
                            timestamp: metadata.lastModifiedAt || date.toISOString(),
                            conversationId: metadata.sessionId,
                            createdAt: metadata.createdAt,
                            lastModifiedAt: metadata.lastModifiedAt,
                            isOwner: metadata.isOwner,
                            isShared: metadata.isShared,
                            currentAgent: metadata.currentAgent,
                            sharedWith: metadata.sharedWith,
                            orchestrationCount: metadata.orchestrationCount,
                        }]
                    };
                }
            });
        } else {
            // For "Mine" tab, use existing logic with chat history
            Object.entries(history?.chatHistory || {}).map(([key, value]) => {
                const metadata = conversationMap.get(key);
                
                const mostRecentMessage = value[value.length - 1];
                const lastTimeStamp = mostRecentMessage.timestamp;
                const date = new Date(lastTimeStamp);

                // Check if date is today, yesterday, within the last 7 days, or older
                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                const last7Days = new Date(today);
                last7Days.setDate(last7Days.getDate() - 7);

                let section = 'Older';
                if (date.toDateString() === today.toDateString()) {
                    section = 'Today';
                } else if (date.toDateString() === yesterday.toDateString()) {
                    section = 'Yesterday';
                }
                else if (date > last7Days) {
                    section = 'Last 7 Days';
                }

                // Format timestamp to be H:MM AM/PM
                const hours = date.getHours();
                const minutes = date.getMinutes();
                const ampm = hours >= 12 ? 'PM' : 'AM';
                let chatDateTimeFormatted = `${hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours)}:${minutes < 10 ? `0${minutes}` : minutes} ${ampm}`;

                if (['Older', 'Last 7 Days'].includes(section)) {
                    const formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
                    chatDateTimeFormatted = `${formattedDate} ${chatDateTimeFormatted}`;
                }

                // Use metadata title if available, otherwise use first message content as fallback
                const contentTitle = metadata?.title || value[0].content;
                console.log(`Processing ${key}: metadata.title="${metadata?.title}", metadata.currentAgent="${metadata?.currentAgent}", firstMessage="${value[0].content.substring(0, 50)}", using="${contentTitle.substring(0, 50)}"`);
                
                // Default to owner if no metadata (backwards compatibility)
                const isOwner = metadata?.isOwner !== false;
                const currentAgent = metadata?.currentAgent;
                console.log(`  -> currentAgent extracted: "${currentAgent}"`);

                if (sectionCache.hasOwnProperty(section)) {
                    sectionCache[section].chats.push({
                        title: contentTitle,
                        time: chatDateTimeFormatted,
                        timestamp: lastTimeStamp,
                        conversationId: key,
                        createdAt: metadata?.createdAt,
                        lastModifiedAt: metadata?.lastModifiedAt,
                        isOwner: isOwner,
                        isShared: metadata?.isShared,
                        currentAgent: currentAgent,
                        sharedWith: metadata?.sharedWith,
                        orchestrationCount: metadata?.orchestrationCount ?? 0,
                    });
                } else {
                    sectionCache[section] = {
                        title: section,
                        chats: [{
                            title: contentTitle,
                            time: chatDateTimeFormatted,
                            timestamp: lastTimeStamp,
                            conversationId: key,
                            createdAt: metadata?.createdAt,
                            lastModifiedAt: metadata?.lastModifiedAt,
                            isOwner: isOwner,
                            isShared: metadata?.isShared,
                            sharedWith: metadata?.sharedWith,
                            currentAgent: currentAgent,
                            orchestrationCount: metadata?.orchestrationCount ?? 0,
                        }]
                    };
                }
            });
        }

        setFormattedHistoryData(Object.values(sectionCache).map(section => ({
            ...section,
            chats: section.chats.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        })));
    }, [history, currentTab, ownedConversations, sharedConversations]);

    const filteredData = formattedHistoryData.map(section => ({
        ...section,
        chats: section.chats.filter(chat => !searchValue.trim() || chat.title.toLowerCase().includes(searchValue.toLowerCase()))
    })).filter(section => section.chats.length > 0)

    const handleChatClick = async (sectionIndex: number, chatIndex: number) => {
        const chat = filteredData[sectionIndex].chats[chatIndex];

        // Check if this is a shared conversation (read-only)
        const isReadOnly = !chat.isOwner;
        
        // Set read-only mode in app state
        appStateContext.dispatch({ type: 'SET_READ_ONLY_MODE', payload: isReadOnly });

        // Use getConversationBySessionId to properly load both conversation AND orchestration state
        try {
            
            //Load conversation and orchestration state from backend
            await appStateContext.getConversationBySessionId(chat.conversationId);

            //load the existing orchState from backend
            const orchState = await appStateContext.getOrchState(chat.conversationId);

            if (orchState) {

                appStateContext.setCurrentOrchestrationType(orchState.currentOrchestrationType!);
                appStateContext.dispatch({ type: 'SET_ORCHESTRATION_STATE', payload: orchState! });

                const currentOrch = getCurrentOrchestration(orchState);

                 if (currentOrch?.currentAgent) {
                    appStateContext.dispatch({ type: "SET_CURRENT_AGENT", payload: currentOrch.currentAgent });
                }

                
              // Check if we should add the continue option based on orchestration type and completion status
            const shouldAddContinueOption = 
                (currentOrch && 
                (currentOrch.type === 0 || currentOrch.type === 1) && // StartNegotiation or ContinueNegotiation
                currentOrch.isComplete === true )??false;

            // Mark that chat is loaded from history
            appStateContext.setShouldAddContinueOption(shouldAddContinueOption);
            //reset the max step to 0 and allow conversation to progress from there
            appStateContext.setMaxStep(0);
            }
    
            
        } catch (error) {
            
            // Fallback to old method if the API call fails
            const itemHistory = history?.chatHistory[chat.conversationId];
            if (itemHistory) {
                const conversation = {
                    sessionId: chat.conversationId,
                    messages: itemHistory.map((v) => ({
                        messageId: v.messageId,
                        content: v.content,
                        authorRole: v.authorRole,
                        timestamp: v.timestamp,
                    })),
                    attachments: []
                };
                appStateContext.dispatch({ type: "SET_CURRENT_CONVERSATION", payload: conversation });
                appStateContext.setShouldAddContinueOption(true);
            }
        }

        if (!Object.keys(appStateContext.state.chatHistory?.chatHistory || {}).includes(appStateContext.state.currentConversation.sessionId ?? '')) {
            appStateContext?.getChatHistory();
        }

        handleHistoryClick();
    }

    const handleHistoryClick = () => {
        appStateContext?.dispatch({ type: 'TOGGLE_CHAT_HISTORY' })
    }

    const newChat = async () => {
        handleHistoryClick()

        appStateContext.dispatch({
            type: 'SET_ACTIVE_DOCUMENT',
            payload: null
        });

        //reset the max step to 0 and allow conversation to progress from there
        appStateContext.setMaxStep(0);

        // Reset read-only mode when starting a new chat (user owns new chats)
        appStateContext.dispatch({ type: 'SET_READ_ONLY_MODE', payload: false });

        // Set the orchestration type to StartNegotiation for new chats
        appStateContext.dispatch({ type: 'SET_CURRENT_ORCHESTRATION_TYPE', payload: 0 });
        
        appStateContext.clearCurrentChat();
        appStateContext?.getChatHistory();
    };

    const animation = useMemo(() => {
        return appStateContext.state.isChatHistoryOpen ? {
            transform: 'translateX(0%)',
            animation: 'right-slide-in 1s backwards',
        } : {
            transform: 'translateX(100%)',
            animation: 'right-slide-out 1s forwards',
        };
    }, [appStateContext.state.isChatHistoryOpen]);

    const onSessionMenuClose = () => {
        setAnchorEl(null);
    }

    const onDelete = async () => {
        try {
            if (!itemToBeDeleted) return

            // Call the function to delete the session history
            await appStateContext.clearSessionHistory(itemToBeDeleted)
        } catch (error) {
            console.error(error)
        } finally {
            setItemToBeDeleted(null)
            setAnchorEl(null)
            setDeleteDialogOpen(false)
            handleHistoryClick()
        }
    }

    return (
        <>
            <Box

                sx={{
                    // prevents animation on page load
                    animationDuration: mount ? '' : '0s !important',
                    transition: mount ? '' : 'background-color 0s, opacity 0s, color 0s, width 0s, height 0s, padding 0s, margin 0s !important',
                    display: "flex",
                    flexDirection: "column",
                    height: "100vh",
                    bgcolor: "background.paper",

                    px: 2,
                    gap: 1.25,
                    overflow: "hidden",
                    maxWidth: '100vw',
                    position: 'absolute',
                    top: 0,
                    width: appStateContext.state.isPhone ? '100vw' : "480px",
                    pt: 2.5,
                    pb: 1.25,
                    ...animation,
                    right: 0,
                    zIndex: 115,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            color: "grey.900",
                            fontWeight: 500,
                        }}
                    >
                        Chat History
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                            size="small"
                            onClick={handleRefresh}
                            disabled={isRefreshing}
                            sx={{
                                borderRadius: "30px",
                                p: 0.5,
                                color: 'primary.main'
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                        <IconButton
                            size="small"
                            sx={{
                                borderRadius: "30px",
                                p: 0.5,
                                color: 'primary.main'
                            }}
                            onClick={handleHistoryClick}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Search Bar */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        py: 2,
                        width: "100%",
                    }}
                >
                    <IconButton
                        onClick={newChat}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 40,
                            height: 40,
                            bgcolor: "#e50c20",
                            borderRadius: 2.5,
                            "&:hover": { bgcolor: "#b8091a" },
                        }}
                    >
                        <AddIcon sx={{ color: "#fff" }} />
                    </IconButton>

                    <Autocomplete
                        freeSolo
                        options={[]}
                        fullWidth
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Search Chats..."
                                variant="outlined"
                                // disabled={appStateContext.state.isChatHistoryLoading || !history}
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                slotProps={{
                                    input: {
                                        endAdornment: <SearchIcon fontSize="small" />,
                                        sx: {
                                            bgcolor: "glacial.main",
                                            borderRadius: "30px",
                                            height: "40px",
                                            padding: '9px !important',
                                            "& fieldset": {
                                                borderColor: "grey.400",
                                            },
                                            "& input": {
                                                fontFamily: "var(--small1-14-400-20-font-family)",
                                                fontSize: "var(--small1-14-400-20-font-size)",
                                                fontWeight: "var(--small1-14-400-20-font-weight)",
                                                lineHeight: "var(--small1-14-400-20-line-height)",
                                                color: "placeholder.main",
                                            },
                                        }
                                    }
                                }}
                            />
                        )}
                    />
                </Box>

                {/* Tabs for Mine / Shared */}
                <Tabs
                    value={currentTab}
                    onChange={(_, newValue) => setCurrentTab(newValue)}
                    sx={{
                        minHeight: '36px',
                        '& .MuiTab-root': {
                            minHeight: '36px',
                            py: 0.5,
                            textTransform: 'none',
                        },
                    }}
                >
                    <Tab label="Mine" value="mine" />
                    <Tab 
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                Shared
                                {sharedConversations.length > 0 && (
                                    <Chip 
                                        label={sharedConversations.length} 
                                        size="small" 
                                        sx={{ height: '18px', fontSize: '0.75rem' }}
                                    />
                                )}
                            </Box>
                        } 
                        value="shared" 
                    />
                </Tabs>

                {/* Chat History Sections */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        width: "100%",
                        overflow: "auto",
                        mb: "-10px",
                    }}
                >
                    {appStateContext.state.isChatHistoryLoading ?
                        <Stack
                            direction={"row"}
                            alignContent={"center"}
                            justifyContent={"center"}
                            spacing={2}
                            alignItems={"center"}
                        >
                            <CircularProgress
                                size={16}
                            />
                            <Typography>
                                Loading chat history
                            </Typography>
                        </Stack> :
                        !history &&
                        <Typography>
                            No chat history available
                        </Typography>
                    }
                    {filteredData.map((section, sectionIndex) => (
                        <Box
                            key={section.title}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                width: "100%",
                            }}
                        >
                            {/* Section Header */}
                            <Box sx={{ width: "100%" }}>
                                <Typography
                                    sx={{
                                        px: 0.5,
                                        py: 1,
                                        color: "grey.700",
                                        fontFamily: "var(--small1-14-400-20-font-family)",
                                        fontSize: "var(--small1-14-400-20-font-size)",
                                        fontWeight: "var(--small1-14-400-20-font-weight)",
                                        lineHeight: "var(--small1-14-400-20-line-height)",
                                    }}
                                >
                                    {section.title}
                                </Typography>
                                <Box sx={{ py: 0.5 }}>
                                    <Divider />
                                </Box>
                            </Box>

                            {/* Chat Cards */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: "100%" }}>
                                {section.chats.map((chat, chatIndex) => {
                                    const isActive = chat.conversationId === appStateContext.state.currentConversation.sessionId;
                                    const lastModified = chat.lastModifiedAt ? new Date(chat.lastModifiedAt) : null;
                                    
                                    // Format date/time based on whether it's today
                                    let formattedDate = chat.time;
                                    if (lastModified) {
                                        const today = new Date();
                                        const isToday = lastModified.toDateString() === today.toDateString();
                                        
                                        const hours = lastModified.getHours();
                                        const minutes = lastModified.getMinutes();
                                        const ampm = hours >= 12 ? 'PM' : 'AM';
                                        const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
                                        const timeString = `${displayHours}:${minutes < 10 ? '0' + minutes : minutes} ${ampm}`;
                                        
                                        if (isToday) {
                                            // Today: show only time
                                            formattedDate = timeString;
                                        } else {
                                            // Other days: show date and time
                                            const dateString = `${lastModified.getMonth() + 1}/${lastModified.getDate()}/${lastModified.getFullYear()}`;
                                            formattedDate = `${dateString} ${timeString}`;
                                        }
                                    }
                                    
                                    return (
                                        <Card
                                            key={`${sectionIndex}-${chatIndex}`}
                                            sx={{
                                                cursor: 'pointer',
                                                borderRadius: 2,
                                                border: isActive ? '2px solid' : '1px solid',
                                                borderColor: isActive ? 'primary.main' : 'grey.300',
                                                bgcolor: isActive ? 'coolActive.main' : 'background.paper',
                                                '&:hover': {
                                                    bgcolor: 'coolActive.main',
                                                    borderColor: 'primary.light',
                                                },
                                                transition: 'all 0.2s ease-in-out',
                                            }}
                                            onClick={() => handleChatClick(sectionIndex, chatIndex)}
                                        >
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                {/* Agent Name, Orchestration Count, and Shared indicator - displayed above title */}
                                                {(chat.currentAgent || (chat.orchestrationCount && chat.orchestrationCount > 0) || chat.isShared) && (
                                                    <Box sx={{ mb: 0.5, display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                                        {chat.currentAgent && (
                                                            <Chip
                                                                label={getAgentDisplayName(chat.currentAgent) || chat.currentAgent}
                                                                size="small"
                                                                sx={{
                                                                    height: '18px',
                                                                    fontSize: '10px',
                                                                    fontWeight: 600,
                                                                    bgcolor: '#e50c20',
                                                                    color: 'white',
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.5px',
                                                                }}
                                                            />
                                                        )}
                                                        {chat.orchestrationCount && chat.orchestrationCount > 0 && (
                                                            <Chip
                                                                label={`Prep ${chat.orchestrationCount}`}
                                                                size="small"
                                                                sx={{
                                                                    height: '18px',
                                                                    fontSize: '10px',
                                                                    fontWeight: 600,
                                                                    bgcolor: '#e50c20',
                                                                    color: 'white',
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.5px',
                                                                }}
                                                            />
                                                        )}
                                                        {chat.isShared && (
                                                            <Chip
                                                                icon={<ShareIcon />}
                                                                label="Shared"
                                                                size="small"
                                                                sx={(theme) => ({
                                                                    height: '18px',
                                                                    fontSize: '10px',
                                                                    bgcolor: 'info.light',
                                                                    color: theme.palette.mode === 'dark' ? 'white' : '#e50c20',
                                                                    '& .MuiChip-icon': {
                                                                        fontSize: '12px',
                                                                        color: theme.palette.mode === 'dark' ? 'white !important' : '#e50c20 !important',
                                                                    },
                                                                })}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                                
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '14px',
                                                            color: isActive ? 'slate.light' : 'grey.900',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            flex: 1,
                                                        }}
                                                    >
                                                        {chat.title}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setAnchorEl(e.currentTarget);
                                                            setItemToBeDeleted(chat.conversationId);
                                                        }}
                                                        sx={{ ml: 1, color: 'grey.600' }}
                                                    >
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'grey.600',
                                                            fontSize: '12px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <span style={{ fontWeight: 500 }}>Last Modified:</span> {formattedDate}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
            <Popover
                id='session-menu-popover'
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={onSessionMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}

                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{
                    width: '142px'
                }}
            >
                <MenuList>
                    <MenuItem
                        sx={{
                            width: '110px',
                            padding: '8px 16px',
                            gap: '8px'
                        }}
                    >
                        <FontAwesomeIcon icon={faPencil} />
                        <Typography variant="body2" color="text.primary">Rename</Typography>
                    </MenuItem>
                    <MenuItem
                        sx={{
                            width: '110px',
                            padding: '8px 16px',
                            gap: '8px'
                        }}
                        onClick={() => {
                            // Find the chat to share
                            const chatToShare = filteredData
                                .flatMap(section => section.chats)
                                .find(chat => chat.conversationId === itemToBeDeleted);
                            
                            if (chatToShare && chatToShare.isOwner) {
                                setItemToBeShared(chatToShare);
                                setShareDialogOpen(true);
                                setAnchorEl(null);
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faShare} />
                        <Typography variant="body2" color="text.primary">Share</Typography>
                    </MenuItem>
                    <MenuItem
                        sx={{
                            width: '110px',
                            padding: '8px 16px',
                            gap: '8px'
                        }}
                        onClick={() => setDeleteDialogOpen(true)}
                    >
                        <FontAwesomeIcon icon={faTrashCan} color="var(--bs-danger)" />
                        <Typography variant="body2" color="error.main">Delete</Typography>
                    </MenuItem>
                </MenuList>
            </Popover>
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: "24px",
                            boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.15)",
                            maxWidth: "440px",
                            width: "100%",
                            m: 0,
                        },
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h5" fontWeight={500}>
                        Delete chat?
                    </Typography>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={() => setDeleteDialogOpen(false)}
                        aria-label="close"
                        sx={{ p: 1 }}
                    >
                        <FontAwesomeIcon icon={faClose} />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ p: 2 }}>
                    <Stack spacing={2}>
                        <Typography variant="body2">
                            This will delete chat{" "}
                            <Box component="span" fontWeight={400}>
                                {'test name'}
                            </Box>
                            .
                        </Typography>

                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2" color="text.secondary">
                                To clear all chats, go to
                            </Typography>
                            <Link href="#" underline="always" variant="body2" color="info.main">
                                settings
                            </Link>
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                    <Button
                        variant="outlined"
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{
                            borderRadius: "8px",
                            px: 3,
                            py: 1.25,
                            textTransform: "none",
                            fontWeight: 500,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onDelete}
                        color="error"
                        sx={{
                            borderRadius: "12px",
                            px: 2,
                            py: 1.25,
                            textTransform: "none",
                            fontWeight: 500,
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Share Dialog */}
            <Dialog
                open={shareDialogOpen}
                onClose={() => {
                    setShareDialogOpen(false);
                    setShareEmails('');
                    setShareError('');
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: "24px",
                            boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.15)",
                            maxWidth: "500px",
                            width: "100%",
                            m: 0,
                        },
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h5" component="span" fontWeight={500}>
                        Share Conversation
                    </Typography>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={() => {
                            setShareDialogOpen(false);
                            setShareEmails('');
                            setShareError('');
                        }}
                        aria-label="close"
                        sx={{ p: 1 }}
                    >
                        <FontAwesomeIcon icon={faClose} />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ p: 2 }}>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            Each recipient will receive an email notification with a link to the site and the shared conversation. Anyone you share this conversation with will have full access to all details contained within it. Please only share with individuals you trust and intend to view the complete content of this conversation.
                        </Typography>

                        <TextField
                            fullWidth
                            label="Email addresses"
                            placeholder="Enter email addresses (comma-separated)"
                            value={shareEmails}
                            onChange={(e) => {
                                setShareEmails(e.target.value);
                                setShareError('');
                            }}
                            multiline
                            rows={3}
                            error={!!shareError}
                            helperText={shareError || "Separate multiple email addresses with commas"}
                        />

                        {itemToBeShared?.sharedWith && itemToBeShared.sharedWith.length > 0 && (
                            <Box>
                                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                                    Currently shared with:
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                    {itemToBeShared.sharedWith.map((email) => (
                                        <Chip
                                            key={email}
                                            label={email}
                                            size="small"
                                            onDelete={() => {
                                                // Handle unshare
                                                httpService.unshareConversation({
                                                    sessionId: itemToBeShared.conversationId,
                                                    emailAddress: email
                                                }, new AbortController().signal).then(() => {
                                                    fetchConversationList();
                                                });
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setShareDialogOpen(false);
                            setShareEmails('');
                            setShareError('');
                        }}
                        sx={{
                            borderRadius: "8px",
                            px: 3,
                            py: 1.25,
                            textTransform: "none",
                            fontWeight: 500,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleShareConversation}
                        sx={{
                            borderRadius: "12px",
                            px: 2,
                            py: 1.25,
                            textTransform: "none",
                            fontWeight: 500,
                            bgcolor: '#e50c20',
                            '&:hover': {
                                bgcolor: '#b8091a',
                            },
                        }}
                    >
                        Send
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MuiChatHistoryBar;
